import { supabase } from '../../services/supabase'
import { hideSimulatedSuppliers } from './simulatedSuppliers'
import type { BrandLineRow } from './dbTableAnswers'
import {
  longestSupplierSearchToken,
  normalizeSupplierName,
  rankSupplierNameMatches,
  supplierSearchTokens,
} from './supplierRiskRatingsTable'

const SELECT_FIELDS =
  'id, name, location, category, industry, brand, overall_score, procurement_type, parent_name, spend_k, dm_em'

export type SupplierNameMatch = BrandLineRow & { id: string }

/** All product-line rows for an Indirect parent (exact parent_name). */
export async function fetchSupplierRowsByParentName(
  parentName: string
): Promise<SupplierNameMatch[]> {
  const parent = parentName.trim()
  if (!parent) return []
  const { data, error } = await supabase
    .from('suppliers')
    .select(SELECT_FIELDS)
    .eq('parent_name', parent)
    .order('name')
    .limit(200)
  if (error) {
    throw new Error(error.message || 'Failed to load suppliers by parent.')
  }
  return hideSimulatedSuppliers(mapRows(data))
}

/**
 * Fetch supplier product-line rows matching a name query (phrase-first, then longest token + all tokens).
 */
export async function fetchSupplierRowsByName(nameQuery: string): Promise<SupplierNameMatch[]> {
  const q = normalizeSupplierName(nameQuery.replace(/&/g, ' '))
  if (!q) return []
  const tokens = supplierSearchTokens(nameQuery)

  // Phrase-first: "MM Packaging" → MM Packaging Colombia S.A.S.
  const phrasePattern = `%${escapeIlike(q)}%`
  const { data: phraseData, error: phraseErr } = await supabase
    .from('suppliers')
    .select(SELECT_FIELDS)
    .ilike('name', phrasePattern)
    .order('name')
    .limit(120)

  if (phraseErr) {
    throw new Error(phraseErr.message || 'Failed to load suppliers.')
  }

  let rows = mapRows(phraseData)
  if (rows.length > 0) {
    return hideSimulatedSuppliers(filterByAllTokens(rows, tokens))
  }

  const primary = longestSupplierSearchToken(nameQuery)
  if (!primary) return []

  const { data, error } = await supabase
    .from('suppliers')
    .select(SELECT_FIELDS)
    .ilike('name', `%${escapeIlike(primary)}%`)
    .order('name')
    .limit(120)

  if (error) {
    throw new Error(error.message || 'Failed to load suppliers.')
  }

  rows = filterByAllTokens(mapRows(data), tokens)

  if (rows.length === 0 && tokens.length > 0 && tokens[0] !== primary) {
    const { data: again, error: err2 } = await supabase
      .from('suppliers')
      .select(SELECT_FIELDS)
      .ilike('name', `%${escapeIlike(tokens[0])}%`)
      .order('name')
      .limit(120)
    if (err2) throw new Error(err2.message || 'Failed to load suppliers.')
    rows = filterByAllTokens(mapRows(again), tokens)
  }

  return hideSimulatedSuppliers(rows)
}

/** Pick a single legal-name key among matches, or null if ambiguous. */
export function resolveUniqueSupplierNameKey(
  nameQuery: string,
  uniqueKeys: string[]
): string | null {
  if (uniqueKeys.length === 0) return null
  const ranked = rankSupplierNameMatches(
    nameQuery,
    uniqueKeys.map((k) => ({ name: k }))
  )
  if (ranked.kind === 'unique') return (ranked.row.name || '').trim().toLowerCase()
  return null
}

export function groupSupplierRowsByName(
  rows: SupplierNameMatch[]
): Map<string, SupplierNameMatch[]> {
  const byName = new Map<string, SupplierNameMatch[]>()
  for (const row of rows) {
    const key = (row.name || '').trim().toLowerCase()
    if (!key) continue
    const list = byName.get(key) ?? []
    list.push(row)
    byName.set(key, list)
  }
  return byName
}

function filterByAllTokens(rows: SupplierNameMatch[], tokens: string[]): SupplierNameMatch[] {
  if (tokens.length <= 1) return rows
  return rows.filter((r) => {
    const n = normalizeSupplierName(r.name || '').toLowerCase()
    return tokens.every((t) => n.includes(t.toLowerCase()))
  })
}

function mapRows(data: unknown): SupplierNameMatch[] {
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    id: typeof r.id === 'string' ? r.id : String(r.id ?? ''),
    name: typeof r.name === 'string' ? r.name : '',
    brand: typeof r.brand === 'string' ? r.brand : null,
    category:
      (typeof r.category === 'string' && r.category) ||
      (typeof r.industry === 'string' && r.industry) ||
      null,
    location: typeof r.location === 'string' ? r.location : null,
    overall_score:
      r.overall_score != null && Number.isFinite(Number(r.overall_score))
        ? Number(r.overall_score)
        : null,
  }))
}

function escapeIlike(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_')
}
